import type { LocalizedString } from "@repo/shared/types";
import {
  type CustomFieldOption,
  MAX_RISK_LEVELS,
  type RiskInputScope,
  type RiskLevel,
  type RiskModelDisplayType,
  type RiskTimeHorizon,
} from "@repo/shared/types";
import { Copy, Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { Breadcrumb } from "@/components/breadcrumb";
import { PageHeader } from "@/components/page-header";
import { AlertMessage } from "@/components/ui/alert-message";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { DocButton } from "@/components/ui/doc-button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { OptionRow } from "@/components/ui/option-row";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ValuePickerDialog } from "@/components/ui/value-picker-dialog";
import { useTabParam } from "@/hooks/use-tab-param";
import { ApiError } from "@/lib/api/_client";
import { useEditFields } from "@/lib/hooks/use-clients";
import {
  useCreateRiskLevel,
  useCreateTimeHorizon,
  useDeleteRiskLevel,
  useDeleteTimeHorizon,
  useRiskModel,
  useUpdateRiskLevel,
  useUpdateRiskModel,
  useUpdateTimeHorizon,
} from "@/lib/hooks/use-risk-models";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { CloneRiskModelDialog } from "../clone-risk-model-dialog";
import { RiskAdjustmentQuestionsEditor } from "./risk-adjustment-questions-editor";
import { RiskBoundsEditor } from "./risk-bounds-editor";
import { RiskMatrixEditor } from "./risk-matrix-editor";
import { RiskModelDetailLoader } from "./risk-model-detail-loader";
import { T } from "./risk-model-editor.i18n";
import { RiskOptionDialog, type RiskOptionSavePayload } from "./risk-option-dialog";

/**
 * QL-212 — one explicit scope choice. "goal" (the default) captures the input
 * once per goal; "global" captures it once for the whole flow instance.
 *
 * `disabled` carries the D14 lock. The reason is rendered once for the whole
 * card rather than as a tooltip on the control: a disabled radio takes no
 * pointer events and is not focusable, so a tooltip on it would be unreachable
 * by keyboard and screen reader.
 */
function ScopeRadioRow({
  id,
  label,
  hint,
  goalLabel,
  globalLabel,
  scope,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  hint: string;
  goalLabel: string;
  globalLabel: string;
  scope: RiskInputScope;
  disabled?: boolean;
  onChange: (scope: RiskInputScope) => void;
}) {
  const labelClass = disabled ? "text-muted-foreground" : undefined;
  return (
    <div className="space-y-2">
      <Label className={labelClass}>{label}</Label>
      <RadioGroup
        value={scope}
        disabled={disabled}
        onValueChange={(value) => onChange(value as RiskInputScope)}
      >
        <OptionRow>
          <RadioGroupItem value="goal" id={`${id}-goal`} />
          <Label htmlFor={`${id}-goal`} className={cn("font-normal cursor-pointer", labelClass)}>
            {goalLabel}
          </Label>
        </OptionRow>
        <OptionRow>
          <RadioGroupItem value="global" id={`${id}-global`} />
          <Label htmlFor={`${id}-global`} className={cn("font-normal cursor-pointer", labelClass)}>
            {globalLabel}
          </Label>
        </OptionRow>
      </RadioGroup>
      <p className="text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}

const TAB_VALUES = ["model", "questions", "settings"] as const;

type RiskModelEditorProps = {
  id: string;
};

export function RiskModelEditor({ id }: RiskModelEditorProps) {
  const localize = useLocalize();
  const t = useT(T);
  const { model, isLoading } = useRiskModel(id);

  // Mutation hooks
  const createHorizon = useCreateTimeHorizon();
  const updateHorizon = useUpdateTimeHorizon();
  const deleteHorizon = useDeleteTimeHorizon();
  const createLevel = useCreateRiskLevel();
  const updateLevel = useUpdateRiskLevel();
  const deleteLevel = useDeleteRiskLevel();
  const updateModel = useUpdateRiskModel();

  // QP-180 — client custom fields for the score-cap selector. The endpoint mixes
  // in standard fields (synthetic "standard-*" ids) which the server rejects —
  // capClientField must reference a real custom_field row. Only fields with
  // enumerable options qualify (the cap triggers on explicit value selection):
  // dropdowns (their configured options) and booleans (stored "true"/"false").
  // /edit returns every active field — cap eligibility is independent of the
  // field's list visibility (the /active endpoint filters on visibleInList).
  const { editFields } = useEditFields();
  const capFieldOptions = editFields.filter(
    (field: { id: string; type?: string }) =>
      !field.id.startsWith("standard-") && (field.type === "dropdown" || field.type === "boolean"),
  );

  /**
   * The enumerable option values of a cap-eligible field. Dropdown options
   * carry stable ids (QL-123): the id is the stored cap value, the localized
   * label is shown. Archived options stay listed while the cap still uses them.
   * Boolean fields persist "true"/"false" (see client-dialog.tsx), so those are
   * the stored cap values — the localized Yes/No is display-only (QL-200).
   */
  const capFieldValueOptions = (
    field: { type?: string; options?: CustomFieldOption[] | null } | undefined,
  ): Array<{ value: string; label: string }> => {
    if (!field) return [];
    if (field.type === "boolean")
      return [
        { value: "true", label: t.yes },
        { value: "false", label: t.no },
      ];
    const selected = model?.capFieldValues ?? [];
    return (field.options ?? [])
      .filter((o) => !o.isArchived || selected.includes(o.id))
      .map((o) => ({
        value: o.id,
        label: localize(o.labelI18n) || o.id,
      }));
  };

  /** Display labels for the currently chosen cap values. */
  const capValueLabels = (
    field: { type?: string; options?: CustomFieldOption[] | null } | undefined,
  ) => {
    const options = capFieldValueOptions(field);
    return (model?.capFieldValues ?? []).map(
      (value) => options.find((o) => o.value === value)?.label || value,
    );
  };

  // Helper to get localized value
  const getLabel = (i18n: LocalizedString) => localize(i18n);

  // QL-144 — controlled tabs so a dirty Questions tab can veto the switch. A
  // requested switch away from dirty questions parks in pendingTab until the
  // user confirms the discard (or cancels and keeps editing).
  const [activeTab, setActiveTab] = useTabParam(TAB_VALUES, "model");
  const [questionsDirty, setQuestionsDirty] = useState(false);
  const [pendingTab, setPendingTab] = useState<string | null>(null);

  const handleTabChange = (value: string) => {
    if (activeTab === "questions" && questionsDirty && value !== "questions") {
      setPendingTab(value);
      return;
    }
    setActiveTab(value);
  };

  // QP-180 — add/edit dialog state per section (null option = create)
  const [horizonDialogOpen, setHorizonDialogOpen] = useState(false);
  const [editingHorizon, setEditingHorizon] = useState<RiskTimeHorizon | null>(null);
  const [levelDialogOpen, setLevelDialogOpen] = useState(false);
  const [editingLevel, setEditingLevel] = useState<RiskLevel | null>(null);

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleteAction, setDeleteAction] = useState<(() => Promise<void>) | null>(null);
  // QL-212 — duplicating is the way out of the scope lock below, so the action
  // sits here as well as in the list, and stays available while the model is active.
  const [cloneOpen, setCloneOpen] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState({
    title: "",
    description: "",
    variant: "default" as "default" | "destructive",
  });

  const showAlert = (
    title: string,
    description: string,
    variant: "default" | "destructive" = "default",
  ) => {
    setAlertConfig({ title, description, variant });
    setAlertOpen(true);
  };

  // QP-180 — the icon SVG is only edited (and rendered) in cards mode
  const isCardsMode = model?.displayType === "cards";

  // Cap value-picker: opens when a cap field is chosen (or re-opened via the
  // "Select values" button). Saving persists field + values together.
  const [capPickerOpen, setCapPickerOpen] = useState(false);
  const [capPickerFieldId, setCapPickerFieldId] = useState<string | null>(null);
  const capPickerField = capFieldOptions.find((f: { id: string }) => f.id === capPickerFieldId);

  const openCapPicker = (fieldId: string) => {
    setCapPickerFieldId(fieldId);
    setCapPickerOpen(true);
  };

  const handleCapPickerSave = async (values: string[]) => {
    if (!capPickerFieldId) return;
    if (values.length === 0) {
      showAlert(t.error, t.capValuesRequired, "destructive");
      return;
    }
    setCapPickerOpen(false);
    await saveModelFields({ capClientField: capPickerFieldId, capFieldValues: values });
  };

  // QP-180 — persist model-level display/cap settings (name/description unchanged)
  const saveModelFields = async (fields: {
    displayType?: RiskModelDisplayType;
    capLevel?: number | null;
    capClientField?: string | null;
    capFieldValues?: string[] | null;
    // QL-212 — scope of each risk-assessment input.
    timeHorizonScope?: RiskInputScope;
    riskLevelScope?: RiskInputScope;
    adjustmentQuestionsScope?: RiskInputScope;
  }) => {
    if (!model) return;
    try {
      await updateModel.mutateAsync({
        id,
        data: {
          nameI18n: model.nameI18n,
          descriptionI18n: model.descriptionI18n ?? undefined,
          ...fields,
        },
      });
    } catch (err) {
      showAlert(
        t.error,
        err instanceof ApiError ? err.message : t.failedToUpdateModel,
        "destructive",
      );
    }
  };

  // Time Horizon handlers — create or update depending on dialog mode.
  // Re-throws so the dialog stays open with drafts intact on failure.
  const handleSaveHorizon = async (payload: RiskOptionSavePayload) => {
    try {
      if (editingHorizon) {
        await updateHorizon.mutateAsync({ thId: editingHorizon.id, data: payload });
      } else {
        await createHorizon.mutateAsync({ modelId: id, data: payload });
      }
    } catch (err) {
      showAlert(
        t.error,
        err instanceof ApiError
          ? err.message
          : editingHorizon
            ? t.failedToUpdateTimeHorizon
            : t.failedToAddTimeHorizon,
        "destructive",
      );
      throw err;
    }
  };

  const handleDeleteHorizon = (horizonId: string) => {
    setDeleteAction(() => async () => {
      try {
        await deleteHorizon.mutateAsync(horizonId);
      } catch (err) {
        showAlert(
          t.error,
          err instanceof ApiError ? err.message : t.failedToDeleteTimeHorizon,
          "destructive",
        );
      }
    });
    setConfirmDeleteOpen(true);
  };

  // Risk Level handlers — create or update depending on dialog mode.
  const handleSaveLevel = async (payload: RiskOptionSavePayload) => {
    try {
      if (editingLevel) {
        await updateLevel.mutateAsync({ rlId: editingLevel.id, data: payload });
      } else {
        await createLevel.mutateAsync({ modelId: id, data: payload });
      }
    } catch (err) {
      showAlert(
        t.error,
        err instanceof ApiError
          ? err.message
          : editingLevel
            ? t.failedToUpdateRiskLevel
            : t.failedToAddRiskLevel,
        "destructive",
      );
      throw err;
    }
  };

  const handleDeleteLevel = (levelId: string) => {
    setDeleteAction(() => async () => {
      try {
        await deleteLevel.mutateAsync(levelId);
      } catch (err) {
        showAlert(
          t.error,
          err instanceof ApiError ? err.message : t.failedToDeleteRiskLevel,
          "destructive",
        );
      }
    });
    setConfirmDeleteOpen(true);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Breadcrumb
          items={[
            { label: t.settings, href: "/settings" },
            { label: t.riskModels, href: "/settings/risk-models" },
            { label: t.loading },
          ]}
        />
        <RiskModelDetailLoader />
      </div>
    );
  }

  if (!model) {
    return (
      <div className="space-y-6">
        <Breadcrumb
          items={[
            { label: t.settings, href: "/settings" },
            { label: t.riskModels, href: "/settings/risk-models" },
            { label: t.notFound },
          ]}
        />
        <div className="text-center py-12 text-muted-foreground">{t.notFound}</div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        <PageHeader
          breadcrumbs={[
            { label: t.settings, href: "/settings" },
            { label: t.riskModels, href: "/settings/risk-models" },
            { label: getLabel(model.nameI18n) },
          ]}
          title={getLabel(model.nameI18n)}
          description={model.descriptionI18n ? getLabel(model.descriptionI18n) : undefined}
          actions={
            <>
              <Badge variant={model.isActive ? "default" : "secondary"}>
                {model.isActive ? t.active : t.inactive}
              </Badge>
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={() => setCloneOpen(true)}
              >
                <Copy className="h-4 w-4" />
                {t.duplicate}
              </Button>
              <DocButton docSlug="creating-models" variant="outline" size="sm" className="gap-2">
                {t.documentation}
              </DocButton>
            </>
          }
        />

        {/* QP-187 — Model tab (horizons/levels/matrix) + Settings tab (display/cap) */}
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList>
            <TabsTrigger value="model">{t.tabModel}</TabsTrigger>
            <TabsTrigger value="questions">
              {t.tabQuestions}
              {questionsDirty && (
                <span
                  className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-primary"
                  aria-hidden="true"
                />
              )}
            </TabsTrigger>
            <TabsTrigger value="settings">{t.tabSettings}</TabsTrigger>
          </TabsList>

          <TabsContent value="model" className="mt-4 space-y-6">
            <div className="grid grid-cols-2 gap-6">
              {/* Time Horizons */}
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle>{t.timeHorizons}</CardTitle>
                    <Badge variant="outline">{t.maxFive}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {model.timeHorizons.map((horizon: RiskTimeHorizon) => (
                    <div
                      key={horizon.id}
                      className="flex items-center justify-between p-3 border rounded"
                    >
                      <span className="truncate">{getLabel(horizon.labelI18n)}</span>
                      <div className="flex items-center shrink-0">
                        <Button
                          size="icon"
                          tooltip={t.editTimeHorizon}
                          variant="ghost"
                          onClick={() => {
                            setEditingHorizon(horizon);
                            setHorizonDialogOpen(true);
                          }}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          tooltip={t.delete}
                          variant="ghost"
                          onClick={() => handleDeleteHorizon(horizon.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}

                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      setEditingHorizon(null);
                      setHorizonDialogOpen(true);
                    }}
                    disabled={model.timeHorizons.length >= 5}
                  >
                    {t.addTimeHorizon}
                  </Button>
                </CardContent>
              </Card>

              {/* Risk Levels */}
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle>{t.riskLevels}</CardTitle>
                    <Badge variant="outline">{t.maxRiskLevels}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {model.riskLevels.map((level: RiskLevel) => (
                    <div
                      key={level.id}
                      className="flex items-center justify-between p-3 border rounded"
                    >
                      <span className="truncate">{getLabel(level.labelI18n)}</span>
                      <div className="flex items-center shrink-0">
                        <Button
                          size="icon"
                          tooltip={t.editRiskLevel}
                          variant="ghost"
                          onClick={() => {
                            setEditingLevel(level);
                            setLevelDialogOpen(true);
                          }}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          tooltip={t.delete}
                          variant="ghost"
                          onClick={() => handleDeleteLevel(level.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}

                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      setEditingLevel(null);
                      setLevelDialogOpen(true);
                    }}
                    disabled={model.riskLevels.length >= MAX_RISK_LEVELS}
                  >
                    {t.addRiskLevel}
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Matrix Editor */}
            {model.timeHorizons.length > 0 && model.riskLevels.length > 0 && (
              <RiskMatrixEditor model={model} />
            )}

            {model.timeHorizons.length === 0 || model.riskLevels.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  {t.matrixHint}
                </CardContent>
              </Card>
            ) : null}
          </TabsContent>

          {/* QP-180 — config-driven adjustment questions */}
          <TabsContent value="questions" className="mt-4 space-y-6">
            <RiskAdjustmentQuestionsEditor
              model={model}
              onError={(title, description) => showAlert(title, description, "destructive")}
              onDirtyChange={setQuestionsDirty}
            />
          </TabsContent>

          <TabsContent value="settings" className="mt-4 space-y-6">
            {/* QP-180 — display type + score cap */}
            <Card>
              <CardHeader>
                <CardTitle>{t.displaySettings}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>{t.displayTypeLabel}</Label>
                  <RadioGroup
                    value={model.displayType}
                    onValueChange={(value) =>
                      saveModelFields({ displayType: value as RiskModelDisplayType })
                    }
                  >
                    <OptionRow>
                      <RadioGroupItem value="dropdown" id="display-type-dropdown" />
                      <Label htmlFor="display-type-dropdown" className="font-normal cursor-pointer">
                        {t.displayDropdown}
                      </Label>
                    </OptionRow>
                    <OptionRow>
                      <RadioGroupItem value="cards" id="display-type-cards" />
                      <Label htmlFor="display-type-cards" className="font-normal cursor-pointer">
                        {t.displayCards}
                      </Label>
                    </OptionRow>
                  </RadioGroup>
                  <p className="text-xs text-muted-foreground">{t.displayTypeHint}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t.capFieldLabel}</Label>
                    <Select
                      value={model.capClientField ?? "none"}
                      onValueChange={(value) => {
                        if (value === "none") {
                          saveModelFields({ capClientField: null, capFieldValues: null });
                        } else {
                          // The cap needs explicit values — pick them before saving.
                          openCapPicker(value);
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t.capNone} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{t.capNone}</SelectItem>
                        {capFieldOptions.map((field: any) => (
                          <SelectItem key={field.id} value={field.id}>
                            {localize(field.nameI18n) || field.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {model.capClientField && (
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => openCapPicker(model.capClientField!)}
                        >
                          {t.selectValues}
                        </Button>
                        <span className="text-xs text-muted-foreground">
                          {t.capValuesLabel}:{" "}
                          {capValueLabels(
                            capFieldOptions.find(
                              (f: { id: string }) => f.id === model.capClientField,
                            ),
                          ).join(", ") || "—"}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>{t.capLevelLabel}</Label>
                    <Input
                      key={`cap-level-${model.capLevel ?? ""}`}
                      type="number"
                      min={1}
                      max={20}
                      defaultValue={model.capLevel ?? ""}
                      onBlur={(e) => {
                        const parsed = Number.parseInt(e.target.value, 10);
                        const next = Number.isNaN(parsed) ? null : parsed;
                        if (next !== (model.capLevel ?? null)) {
                          saveModelFields({ capLevel: next });
                        }
                      }}
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">{t.capHint}</p>
              </CardContent>
            </Card>

            {/* QL-212 — who each risk-assessment input belongs to */}
            <Card>
              <CardHeader>
                <CardTitle>{t.scopeSettings}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-xs text-muted-foreground">{t.scopeHint}</p>
                {/* QL-212 (D14) — the scopes freeze once the model is active,
                    so no save is attempted rather than one failing server-side. */}
                {model.isActive && <p className="text-xs text-warning">{t.scopeLocked}</p>}
                <ScopeRadioRow
                  id="risk-scope-time-horizon"
                  label={t.scopeTimeHorizonLabel}
                  hint={t.scopeTimeHorizonHint}
                  goalLabel={t.scopeGoal}
                  globalLabel={t.scopeGlobal}
                  scope={model.timeHorizonScope}
                  disabled={model.isActive || updateModel.isPending}
                  onChange={(scope) => saveModelFields({ timeHorizonScope: scope })}
                />
                <ScopeRadioRow
                  id="risk-scope-risk-level"
                  label={t.scopeRiskLevelLabel}
                  hint={t.scopeRiskLevelHint}
                  goalLabel={t.scopeGoal}
                  globalLabel={t.scopeGlobal}
                  scope={model.riskLevelScope}
                  disabled={model.isActive || updateModel.isPending}
                  onChange={(scope) => saveModelFields({ riskLevelScope: scope })}
                />
                <ScopeRadioRow
                  id="risk-scope-adjustment-questions"
                  label={t.scopeAdjustmentQuestionsLabel}
                  hint={t.scopeAdjustmentQuestionsHint}
                  goalLabel={t.scopeGoal}
                  globalLabel={t.scopeGlobal}
                  scope={model.adjustmentQuestionsScope}
                  disabled={model.isActive || updateModel.isPending}
                  onChange={(scope) => saveModelFields({ adjustmentQuestionsScope: scope })}
                />
              </CardContent>
            </Card>

            {/* GAP-028 — custom-portfolio risk bounds */}
            <RiskBoundsEditor
              model={model}
              onError={(title, description) => showAlert(title, description, "destructive")}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* QP-180 — shared add/edit dialogs with label/description/icon + translations */}
      <RiskOptionDialog
        open={horizonDialogOpen}
        onOpenChange={(open) => {
          setHorizonDialogOpen(open);
          if (!open) setEditingHorizon(null);
        }}
        option={editingHorizon}
        showIconField={isCardsMode}
        title={editingHorizon ? t.editTimeHorizon : t.addTimeHorizon}
        labelPlaceholder={t.addTimeHorizonPlaceholder}
        onSave={handleSaveHorizon}
      />

      <RiskOptionDialog
        open={levelDialogOpen}
        onOpenChange={(open) => {
          setLevelDialogOpen(open);
          if (!open) setEditingLevel(null);
        }}
        option={editingLevel}
        showIconField={isCardsMode}
        title={editingLevel ? t.editRiskLevel : t.addRiskLevel}
        labelPlaceholder={t.addRiskLevelPlaceholder}
        onSave={handleSaveLevel}
      />

      {/* QL-212 — duplicate this model; the copy starts inactive and unlocks its scopes */}
      <CloneRiskModelDialog open={cloneOpen} model={model} onClose={() => setCloneOpen(false)} />

      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title={t.deleteItemTitle}
        description={t.deleteItemDescription}
        confirmText={t.delete}
        cancelText={t.cancel}
        onConfirm={async () => {
          if (deleteAction) {
            await deleteAction();
          }
        }}
        variant="destructive"
      />

      {/* QL-144 — leaving a dirty Questions tab discards the draft; confirm first */}
      <ConfirmDialog
        open={pendingTab !== null}
        onOpenChange={(open) => {
          if (!open) setPendingTab(null);
        }}
        title={t.unsavedChangesTitle}
        description={t.unsavedChangesDescription}
        confirmText={t.discardAndLeave}
        cancelText={t.keepEditing}
        onConfirm={async () => {
          if (pendingTab) {
            // The editor unmounts on switch, which discards its draft.
            setQuestionsDirty(false);
            setActiveTab(pendingTab);
            setPendingTab(null);
          }
        }}
        variant="destructive"
      />

      <AlertMessage
        open={alertOpen}
        onOpenChange={setAlertOpen}
        title={alertConfig.title}
        description={alertConfig.description}
        variant={alertConfig.variant}
      />

      {/* QP-180 — cap value selection (client-view custom-filter style) */}
      <ValuePickerDialog
        open={capPickerOpen}
        options={capFieldValueOptions(capPickerField)}
        selectedValues={
          model?.capClientField === capPickerFieldId ? (model?.capFieldValues ?? []) : []
        }
        labels={{
          title: t.selectTitle(
            capPickerField ? localize(capPickerField.nameI18n) || capPickerField.name : "",
          ),
          description: (count) => t.selectDescription(String(count)),
        }}
        onClose={() => setCapPickerOpen(false)}
        onSave={handleCapPickerSave}
      />
    </>
  );
}
